import { memo, useMemo } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { More } from "./radarUi";
import {
  echoList,
  finite,
  formatMm as mm,
  levelAt,
  levelDomain,
  levelTicks,
  pollValue,
} from "utils/radarDiagnostics";
import {
  ASSUMED_STEP_MM,
  ECHO_SNR_DB,
  NEAR_FIELD_BINS,
  WIDTH_DROP_DB,
  harmonicOf,
  pickDistance,
  spectrumReachMm,
  validPeakBin,
} from "utils/radarSignal";
import { captureArrival, hasSpectrum, spectrumRows } from "utils/radarStream";

// Radar live: one capture's spectrum (dB against reported distance) with the
// radar's own pick, its peak bin, echo list and threshold curve, and our quick
// analysis (noise floor, strongest echoes, the chosen echo's -6 dB width,
// near-field energy) beside it.
export const LIVE_COLORS = {
  spectrum: "#3d5866",
  threshold: "#a8327a",
  radar: "#c8571f",
  ours: "#087f70",
  echo: "#172d39",
  floor: "#8aa0aa",
  near: "#fdecc8",
};
export const NEAR_RANGE_MM = 2000;
const LETTERS = "ABCDE";

export const axisOf = (capture) => {
  const step = finite(capture?.step_mm);
  const cal = finite(capture?.cal_mm);
  return {
    step: step || ASSUMED_STEP_MM,
    cal: cal ?? 0,
    assumedStep: !step,
    assumedCal: cal === null,
  };
};

// 0-2 m, or every bin: bin 0 sits below zero by the calibration offset.
export function spectrumDomain(capture, range) {
  if (range !== "full") return [0, NEAR_RANGE_MM];
  const { step, cal } = axisOf(capture);
  const bins = Array.isArray(capture?.db) ? capture.db.length : 256;
  return [Math.min(0, -cal), (bins - 1) * step - cal];
}
export const ticksFor = ([lo, hi]) => {
  const every = hi - lo > 3000 ? 1000 : 250;
  const ticks = [];
  for (let v = Math.ceil(lo / every) * every; v <= hi; v += every)
    ticks.push(v + 0);
  return ticks;
};
const signed = (v, digits = 1) => {
  const r = Number(v.toFixed(digits));
  return `${r > 0 ? "+" : r < 0 ? "−" : "±"}${Math.abs(r).toFixed(digits)}`;
};
const db1 = (v) => `${v.toFixed(1)} dB`;
const sentence = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// A small label on a marker, with a white halo so it stays legible on the
// curve: the radar's echo ranks above their dots, our letters to the right of
// their rings (the two often mark the same echo).
export const markerLabel =
  (text, color, beside = false) =>
  ({ viewBox }) =>
    viewBox ? (
      <text
        x={viewBox.x + viewBox.width / 2 + (beside ? 9 : 0)}
        y={viewBox.y + (beside ? viewBox.height / 2 + 3 : -4)}
        textAnchor={beside ? "start" : "middle"}
        fontSize={10}
        fontWeight={700}
        fill={color}
        stroke="#ffffff"
        strokeWidth={3}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {text}
      </text>
    ) : null;

const SpectrumChart = memo(function SpectrumChart({
  rows,
  domain,
  hasThreshold,
  verticals,
  floor,
  nearArea,
  echoes,
  peaks,
  chosen,
}) {
  const yDomain = levelDomain(rows.flatMap((r) => [r.db, r.thr]));
  return (
    <div className="field-stream-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 16, right: 14, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="d"
            type="number"
            domain={domain}
            ticks={ticksFor(domain)}
            allowDataOverflow
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            domain={yDomain}
            ticks={levelTicks(yDomain)}
            tickFormatter={(v) => Math.round(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" dB"
            width={62}
          />
          <Tooltip
            labelFormatter={(v) => `${Math.round(v).toLocaleString()} mm`}
            formatter={(value, name) => [
              value === null || value === undefined ? "—" : db1(Number(value)),
              name,
            ]}
          />
          {nearArea && (
            <ReferenceArea
              x1={nearArea[0]}
              x2={nearArea[1]}
              fill={LIVE_COLORS.near}
              fillOpacity={0.9}
              ifOverflow="hidden"
            />
          )}
          {floor !== null && (
            <ReferenceLine
              y={floor}
              stroke={LIVE_COLORS.floor}
              strokeWidth={1.5}
              strokeDasharray="2 3"
            />
          )}
          {hasThreshold && (
            <Line
              dataKey="thr"
              name="Radar's threshold"
              stroke={LIVE_COLORS.threshold}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          <Line
            dataKey="db"
            name="Spectrum"
            stroke={LIVE_COLORS.spectrum}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
          {verticals.map((v) => (
            <ReferenceLine
              key={v.key}
              x={v.x}
              stroke={v.color}
              strokeWidth={2}
              strokeDasharray={v.dash}
            />
          ))}
          {chosen && (
            <ReferenceLine
              segment={[
                { x: chosen.leftMm, y: chosen.level - WIDTH_DROP_DB },
                { x: chosen.rightMm, y: chosen.level - WIDTH_DROP_DB },
              ]}
              stroke={LIVE_COLORS.ours}
              strokeWidth={3}
            />
          )}
          {echoes.map((e) => (
            <ReferenceDot
              key={`echo-${e.rank}`}
              x={e.rangeMm}
              y={e.db}
              r={4.5}
              fill={LIVE_COLORS.echo}
              stroke="#ffffff"
              strokeWidth={2}
              label={markerLabel(e.rank, LIVE_COLORS.echo)}
            />
          ))}
          {peaks.map((p) => (
            <ReferenceDot
              key={`ours-${p.letter}`}
              x={p.mm}
              y={p.level}
              r={5}
              fill="#ffffff"
              stroke={LIVE_COLORS.ours}
              strokeWidth={2}
              label={markerLabel(p.letter, LIVE_COLORS.ours, true)}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

export function Key({ kind = "solid", color, children }) {
  return (
    <li>
      <span
        className={`field-radar-key is-${kind}`}
        style={color ? { "--key": color } : undefined}
        aria-hidden="true"
      />
      {children}
    </li>
  );
}

export function Stat({ label: name, value, detail }) {
  return (
    <div className="field-live-stat" role="group" aria-label={name}>
      <span>{name}</span>
      <b>{value}</b>
      {detail && <small>{detail}</small>}
    </div>
  );
}

// Why a capture has no spectrum, as far as it tells.
function noSpectrumText(capture) {
  const a = captureArrival(capture);
  const sent =
    a.spectrum === 0
      ? "no spectrum packet"
      : a.spectrum === 1
        ? "a spectrum packet without usable levels"
        : `${a.spectrum} spectrum packets without usable levels`;
  const modeNote =
    a.mode === 2
      ? " Mode 2 sends only the beat signal; mode 1 sends the spectrum."
      : a.mode === 3
        ? " Mode 3 sends nothing on this radar."
        : "";
  const why =
    a.mode !== null && a.counted
      ? `In raw output mode ${a.mode} the radar sent ${sent}${a.adc ? "; the beat signal it sent is below" : ""}.${modeNote}`
      : "The spectrum comes only when Spectrum is ticked and the radar's raw output mode sends one (mode 1 on node 1's radar).";
  return `No spectrum in this capture. ${why} Its echo list, if any, is in the capture inspector below.`;
}

export default function RadarSpectrum({
  capture,
  analysis,
  range = "near",
  unit = null,
  echoAnswered = false,
  finer = null,
  clip = null,
}) {
  const axis = axisOf(capture);
  const domain = useMemo(
    () => spectrumDomain(capture, range),
    [capture, range],
  );
  const rows = useMemo(
    () => spectrumRows(capture, domain[0], domain[1]),
    [capture, domain],
  );
  const inView = (x) => x !== null && x >= domain[0] && x <= domain[1];
  const hasThreshold = rows.some((r) => r.thr !== null);
  // The radar's pick and peak bin, unless a bad decode made them nonsense
  // (0 mm, beyond the spectrum, peak bin 25882): then no marker at all.
  const bins = Array.isArray(capture?.db) ? capture.db.length : 256;
  const pickMm =
    analysis?.pick?.source === "distance" ? analysis.pick.mm : null;
  const peakBin = validPeakBin(capture?.peak_bin, bins);
  const peakBinMm = peakBin === null ? null : peakBin * axis.step - axis.cal;
  const verticals = [
    inView(pickMm) && { key: "pick", x: pickMm, color: LIVE_COLORS.radar },
    inView(peakBinMm) && {
      key: "bin",
      x: peakBinMm,
      color: LIVE_COLORS.radar,
      dash: "4 3",
    },
    finer &&
      inView(finer.mm) && {
        key: "finer",
        x: finer.mm,
        color: LIVE_COLORS.ours,
        dash: "2 2",
      },
  ].filter(Boolean);
  const points = useMemo(
    () => rows.filter((r) => r.db !== null).map((r) => ({ r: r.d, db: r.db })),
    [rows],
  );
  const list = echoList(capture?.peaks);
  const echoes = (list?.echoes || [])
    .filter((e) => inView(e.rangeMm))
    .map((e) => ({ ...e, db: levelAt(points, e.rangeMm) }))
    .filter((e) => e.db !== null);
  const peaks = (analysis?.peaks || [])
    .map((p, i) => ({ ...p, letter: LETTERS[i] }))
    .filter((p) => inView(p.mm));
  const chosen = analysis?.chosen;
  const width =
    chosen &&
    chosen.leftMm !== null &&
    inView(chosen.leftMm) &&
    inView(chosen.rightMm)
      ? chosen
      : null;
  const nearEnd = (NEAR_FIELD_BINS - 0.5) * axis.step - axis.cal;
  const nearArea =
    nearEnd > domain[0]
      ? [Math.max(domain[0], -0.5 * axis.step - axis.cal), nearEnd]
      : null;

  if (!rows.some((r) => r.db !== null))
    return (
      <div className="field-radar-empty">
        {hasSpectrum(capture)
          ? "This capture has no levels in this range; try the full range."
          : noSpectrumText(capture)}
      </div>
    );
  return (
    <div className="field-stream-spectrum">
      <p className="field-radar-axis">
        {unit ||
          "dB = 20·log10(bin value), assuming linear magnitude (unconfirmed)"}
      </p>
      <SpectrumChart
        rows={rows}
        domain={domain}
        hasThreshold={hasThreshold}
        verticals={verticals}
        floor={analysis?.floor ?? null}
        nearArea={nearArea}
        echoes={echoes}
        peaks={peaks}
        chosen={width}
      />
      <p className="field-radar-axis is-x">
        Distance in mm as the node reports it: bin × {mm(axis.step, 2)}
        {axis.assumedStep ? " (assumed)" : ""} − {mm(axis.cal, 1)} calibration
        offset{axis.assumedCal ? " (not reported)" : ""}
      </p>
      <ul className="field-radar-legend" aria-label="Spectrum key">
        <Key color={LIVE_COLORS.spectrum}>Spectrum</Key>
        {hasThreshold && (
          <Key kind="dashed" color={LIVE_COLORS.threshold}>
            Radar's threshold
          </Key>
        )}
        {pickMm !== null && (
          <Key color={LIVE_COLORS.radar}>Radar's pick {mm(pickMm, 1)}</Key>
        )}
        {peakBinMm !== null && (
          <Key kind="dashed" color={LIVE_COLORS.radar}>
            Radar's peak bin {peakBin} ({mm(peakBinMm)})
          </Key>
        )}
        {echoes.length > 0 && (
          <Key kind="dot">Radar's echo list (1 = strongest)</Key>
        )}
        {analysis?.floor !== null && analysis?.floor !== undefined && (
          <Key kind="dotted" color={LIVE_COLORS.floor}>
            Noise floor (ours) {db1(analysis.floor)}
          </Key>
        )}
        {peaks.length > 0 && (
          <Key kind="ring" color={LIVE_COLORS.ours}>
            Our echoes {peaks.map((p) => p.letter).join(" ")}
          </Key>
        )}
        {width && (
          <Key kind="bar" color={LIVE_COLORS.ours}>
            Chosen echo's −{WIDTH_DROP_DB} dB width
          </Key>
        )}
        {nearArea && (
          <Key kind="area" color={LIVE_COLORS.near}>
            Near field (bins 0–{NEAR_FIELD_BINS - 1})
          </Key>
        )}
        {finer && inView(finer.mm) && (
          <Key kind="dotted" color={LIVE_COLORS.ours}>
            From the beat signal {mm(finer.mm, 1)}
          </Key>
        )}
      </ul>
      <SpectrumReadout
        capture={capture}
        analysis={analysis}
        echoAnswered={echoAnswered}
        finer={finer}
        clip={clip}
      />
    </div>
  );
}

// Our analysis of a spectrum beside the radar's own figures: the radar's pick
// and peak bin, our chosen echo (SNR, −6 dB width, distance from the pick),
// the noise floor, near-field energy, echoes over the threshold, and our
// strongest echoes as a table. `pick` replaces the radar's-pick figure
// ({label, value, detail}), as for a burst's mean spectrum. A parabola on the
// radar's unwindowed spectrum only approximates an echo's position (nearby
// echoes bias it: node 1's water read 816 mm so, 789 mm from the beat
// signal); `finer` ({bin, mm, source}), from a beat signal of the same burst
// or minute, is preferred. `clip`: the beat signal clips, so echoes at 2-4
// times the strongest one's range may be harmonics.
export function SpectrumReadout({
  capture,
  analysis,
  echoAnswered = false,
  pick = null,
  finer = null,
  clip = null,
}) {
  const axis = axisOf(capture);
  const bins = Array.isArray(capture?.db) ? capture.db.length : 256;
  const reach = spectrumReachMm(axis.step, axis.cal, bins);
  const air = finite(capture?.air_mm);
  const sentinel = air !== null && pollValue(air).sentinel;
  const badAir = air !== null && !sentinel && pickDistance(air, reach) === null;
  const reportedBin = finite(capture?.peak_bin);
  const peakBin = validPeakBin(reportedBin, bins);
  const peakBinMm = peakBin === null ? null : peakBin * axis.step - axis.cal;
  const list = echoList(capture?.peaks);
  const chosen = analysis?.chosen;
  const near = analysis?.nearField;
  const harmonics = clip?.clipped
    ? harmonicOf(analysis?.peaks || [], { base: finer?.bin ?? null })
    : null;
  return (
    <>
      <div className="field-stream-readout">
        {pick ? (
          <Stat label={pick.label} value={pick.value} detail={pick.detail} />
        ) : (
          <Stat
            label="Radar's pick"
            value={
              air === null
                ? "No distance"
                : sentinel
                  ? sentence(pollValue(air).text)
                  : badAir
                    ? "No valid pick"
                    : mm(air, 1)
            }
            detail={
              [
                badAir
                  ? `It reported ${mm(air, 1)}${air > 0 ? `, beyond the spectrum's ${mm(reach)}` : ""}`
                  : null,
                peakBin !== null
                  ? `Its peak bin ${peakBin} = ${mm(peakBinMm)}`
                  : reportedBin !== null
                    ? `Its peak bin ${reportedBin.toLocaleString()} is outside 0–${bins - 1}`
                    : sentinel
                      ? "A status code, not a distance"
                      : null,
              ]
                .filter(Boolean)
                .join(" · ") || null
            }
          />
        )}
        <Stat
          label="Chosen echo (ours)"
          value={
            finer ? mm(finer.mm, 1) : chosen ? mm(chosen.mm, 1) : "No peak"
          }
          detail={
            finer
              ? [
                  `From the beat signal${finer.source ? ` ${finer.source}` : ""} (Hann ×8 zero-padded, bin ${finer.bin.toFixed(2)})`,
                  analysis?.pick
                    ? `${signed(finer.mm - analysis.pick.mm)} mm from the radar's ${analysis.pick.source === "distance" ? "pick" : "peak bin"}`
                    : null,
                  chosen
                    ? `the spectrum alone gives ${mm(chosen.mm, 1)}, approximate (unwindowed spectrum - nearby echoes bias it)`
                    : null,
                  chosen?.snr !== null && chosen?.snr !== undefined
                    ? `SNR ${db1(chosen.snr)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : chosen
                ? [
                    chosen.snr !== null ? `SNR ${db1(chosen.snr)}` : null,
                    chosen.widthMm !== null
                      ? `−${WIDTH_DROP_DB} dB width ${mm(chosen.widthMm)}`
                      : `no −${WIDTH_DROP_DB} dB point on both sides`,
                    analysis.pick
                      ? `${signed(chosen.mm - analysis.pick.mm)} mm from the radar's ${analysis.pick.source === "distance" ? "pick" : "peak bin"}`
                      : null,
                    "approximate (unwindowed spectrum - nearby echoes bias it; the beat signal gives a finer estimate)",
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : analysis?.pick
                  ? "No local maximum within 2 bins of the radar's pick"
                  : "The radar gave no valid pick to compare"
          }
        />
        <Stat
          label="Noise floor (ours)"
          value={
            analysis?.floor !== null && analysis?.floor !== undefined
              ? db1(analysis.floor)
              : "—"
          }
          detail={`Median of bins ${NEAR_FIELD_BINS}–${(capture?.db?.length || 256) - 1}`}
        />
        <Stat
          label={`Near field (bins 0–${NEAR_FIELD_BINS - 1})`}
          value={
            near?.overFloor !== null && near?.overFloor !== undefined
              ? `${signed(near.overFloor)} dB`
              : "—"
          }
          detail={
            near ? `Over the floor · ${db1(near.level)} on the spectrum` : null
          }
        />
        <Stat
          label="Echoes over the threshold"
          value={analysis ? analysis.echoes.count.toLocaleString() : "—"}
          detail={[
            analysis?.echoes.by === "radar"
              ? "Against the radar's threshold"
              : `${ECHO_SNR_DB} dB over the floor (ours)`,
            // Stream rows always carry an echo list, empty when none was asked
            // for (or the radar has none): count it only when there is one.
            list?.echoes.length || echoAnswered
              ? `echo list: ${list?.echoes.length ?? 0}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        />
      </div>
      {analysis?.peaks.length > 0 && (
        <More
          label={`Our echoes (${analysis.peaks.length.toLocaleString()}) and how they are found`}
        >
          <div className="field-stream-table">
            <table className="field-table" aria-label="Our echoes">
              <thead>
                <tr>
                  <th scope="col">Echo</th>
                  <th scope="col">Distance</th>
                  <th scope="col">Level</th>
                  <th scope="col">SNR</th>
                  <th scope="col">−{WIDTH_DROP_DB} dB width</th>
                  {harmonics && <th scope="col">Note</th>}
                </tr>
              </thead>
              <tbody>
                {analysis.peaks.map((p, i) => (
                  <tr key={p.index}>
                    <td>{LETTERS[i]}</td>
                    <td>{mm(p.mm, 1)}</td>
                    <td>{db1(p.level)}</td>
                    <td>{p.snr === null ? "—" : db1(p.snr)}</td>
                    <td>{p.widthMm === null ? "—" : mm(p.widthMm)}</td>
                    {harmonics && (
                      <td>
                        {harmonics[i]
                          ? `possible harmonic (clipping, ${harmonics[i]}×)`
                          : "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="field-caption">
              Ours, strongest first: local maxima at least {ECHO_SNR_DB} dB over
              the floor with 3 dB of prominence (6 dB in the first 8 bins),
              positioned to a fraction of a bin by a parabola through three bins
              (approximate on the radar's unwindowed spectrum). No width where
              the curve does not fall {WIDTH_DROP_DB} dB on both sides (the near
              field, the spectrum's ends, a neighbouring echo).
              {harmonics
                ? " The beat signal clips: echoes at 2-4 times the strongest one's range (within 1.5 bins) may be its harmonics."
                : ""}
              {analysis.peakCount > analysis.peaks.length
                ? ` ${analysis.peakCount - analysis.peaks.length} weaker not listed.`
                : ""}
            </p>
          </div>
        </More>
      )}
    </>
  );
}
